import { DatePipe, NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, output, TrackByFunction } from '@angular/core'
import { ArchivedItemType } from '@app/v2/event/enums/archived-items.enum'
import { ArchivedItem } from '@app/v2/event/types/archived-item.type'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { GenericTableComponent } from '@app/v2/shared/components/generic-table/generic-table.component'
import { GENERIC_TABLE_MAX_VIEWPORT_HEIGHT_PX, GENERIC_TABLE_ROW_HEIGHT_PX } from '@app/v2/shared/components/generic-table/generic-table.constants'
import { TruncationTooltipComponent } from '@app/v2/shared/components/truncation-tooltip/truncation-tooltip.component'
import { GenericTableCellDefDirective } from '@app/v2/shared/directives/generic-table-cell-def.directive'
import { GenericTableColumnDirective } from '@app/v2/shared/directives/generic-table-column.directive'
import { GenericTableSelectionChange, GenericTableSort } from '@app/v2/shared/types/generic-table.types'
import { computeGenericTableViewportHeight } from '@app/v2/shared/utils/generic-table-viewport.util'

@Component({
  selector: 'app-desktop-archived-items-table',
  standalone: true,
  imports: [GenericTableComponent, GenericTableColumnDirective, GenericTableCellDefDirective, DropdownComponent, DatePipe, TruncationTooltipComponent],
  templateUrl: './desktop-archived-items-table.component.html',
  styleUrl: './desktop-archived-items-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DesktopArchivedItemsTableComponent {
  public readonly items = input.required<ArchivedItem[]>()
  public readonly selectedKeys = input.required<string[]>()
  public readonly sort = input.required<GenericTableSort>()
  public readonly hasMore = input.required<boolean>()
  public readonly isLoadingMore = input.required<boolean>()
  public readonly perPage = input.required<number>()
  public readonly trackBy = input.required<TrackByFunction<ArchivedItem>>()
  public readonly rowKey = input.required<(item: ArchivedItem) => string>()
  public readonly menuOptions = input.required<DropdownOption[]>()
  public readonly virtualScrollMinBufferPx = input(300)
  public readonly virtualScrollMaxBufferPx = input(600)

  public readonly selectionChange = output<GenericTableSelectionChange<ArchivedItem>>()
  public readonly sortChange = output<GenericTableSort>()
  public readonly loadMore = output<void>()
  public readonly menuOptionClick = output<{ item: ArchivedItem; option: DropdownOption }>()

  public readonly archivedItemType = ArchivedItemType
  public readonly tableRowHeightPx = GENERIC_TABLE_ROW_HEIGHT_PX

  /**
   * Forward row action menu selection to the parent.
   * @param item {ArchivedItem} - The archived item.
   * @param option {DropdownOption} - The dropdown option.
   */
  public onMenuOptionClick(item: ArchivedItem, option: DropdownOption): void {
    this.menuOptionClick.emit({ item, option })
  }

  public readonly viewportHeightPx = computed(() =>
    computeGenericTableViewportHeight(this.items().length, this.perPage(), GENERIC_TABLE_ROW_HEIGHT_PX, GENERIC_TABLE_MAX_VIEWPORT_HEIGHT_PX, this.hasMore())
  )
}
